import React, { useState, useEffect, useRef } from "react";
import {
    StyledContainer,
    StyledContentWrapper,
    AmenitiesWrapper,
    Title,
    SubTitle,
    BodyText,
    StyledDivider,
    StyledButton,
    AmenityContainer,
} from "./StyledComponents";
import Amenity from "./Amenity/Amenity";
import WatchVideo from "../../assets/SVGComponents/WatchVideo";
import Coffee from "../../assets/SVGComponents/Coffee";
import WatchVideoModal from "../CouponWidget/Navbar/WatchVideoModal";
import { findLinkByName } from "../../assets/store/findLink";
import convertVideoLink from "../../assets/store/convertVideoLinktoEmbed";
import { formatText } from "../../assets/store/formatText";

function Amenities({
    amenities,
    rowData,
    divId,
    rowId,
    links,
    defaultFontFamily,
    dataAttributes,
    client,
    oem,
    isTableRow,
    theme,
}) {
    const {
        primary_button_color,
        primary_hover_button_color,
        primary_button_text_color,
        primary_hover_button_text_color,
        hide_cta_icons,
    } = oem || {
        primary_button_color: null,
        primary_hover_button_color: null,
        primary_button_text_color: null,
        primary_hover_button_text_color: null,
    };

    const { enable_interactive_video } = client;

    const { interactive_video_links } = rowData;

    const {
        background_color,
        button_background_color,
        button_background_hover_color,
        button_text_color,
        button_text_hover_color,
        text_color,
        text_align,
        link_color,
        link_hover_color,
        padding,
        margin,
        title_letter_spacing,
        body_text_letter_spacing,
        line_height,
        text_transform,
        button_border_color,
        button_border_radius,
        button_letter_spacing,
    } = theme || {
        background_color: "transparent",
        button_background_color: primary_button_color,
        button_background_hover_color: primary_hover_button_color,
        button_text_color: primary_button_text_color,
        button_text_hover_color: primary_hover_button_text_color,
        text_color: "#000000",
        text_align: "center",
        link_color: primary_button_color,
        link_hover_color: primary_hover_button_color,
        padding: "0px",
        margin: "0px",
    };

    const containerRef = useRef(null);
    const [isSingleColumn, setIsSingleColumn] = useState(false);

    const [screenSize, setScreenSizes] = useState(
        window?.window?.deviceDetect?.getWindowData() || {
            viewWidth: document.documentElement.clientWidth,
            isMobile: false,
            isDesktop: false,
            isTablet: false,
        }
    );
    const { isMobile } = window?.window?.deviceDetect?.getWindowData();
    const [activeVideo, setActiveVideo] = useState(false);
    const [amenitiesButtonLink, setAmenitiesButtonLink] = useState(null);
    const [amenitiesWatchVideo, setAmenitiesWatchVideo] = useState(false);
    const [dataMediaLink, setDataMediaLink] = useState(null);
    const [linkTarget, setLinkTarget] = useState("_self");
    const { column_1, column_2, column_3, header, body_text } = amenities;
    const amenitiesData = [
        ...JSON.parse(column_1),
        ...JSON.parse(column_2),
        ...JSON.parse(column_3),
    ];

    const half = Math.ceil(amenitiesData.length / 2);
    const amenitiesFirstHalf = amenitiesData.slice(0, half);
    const amenitiesSecondHalf = amenitiesData.slice(half);

    const handleVideoClick = () => {
        amenitiesWatchVideo
            ? setActiveVideo(true)
            : window.open(amenitiesButtonLink, linkTarget).focus();
    };

    useEffect(() => {
        if (screenSize.viewWidth < 600) return setIsSingleColumn(true);
        return setIsSingleColumn(false);
    }, [screenSize]);

    useEffect(() => {
        const whyServiceHereLink = findLinkByName({
            links,
            linkType: "Why Service Here",
        });
        const whyServiceHereVideoLink = findLinkByName({
            links,
            linkType: "Why Service Here Video",
        });
        let interactiveVideoLink =
            enable_interactive_video && interactive_video_links
                ? interactive_video_links[isMobile ? "mobile" : "desktop"][
                      "wsh"
                  ]
                : null;

        interactiveVideoLink = interactiveVideoLink
            ? interactiveVideoLink
            : null;

        setAmenitiesButtonLink(
            interactiveVideoLink
                ? interactiveVideoLink
                : whyServiceHereVideoLink
                ? convertVideoLink(whyServiceHereVideoLink.link)
                : whyServiceHereLink
                ? whyServiceHereLink.link
                : null
        );
        if (
            (whyServiceHereVideoLink && whyServiceHereVideoLink.link) ||
            interactiveVideoLink
        ) {
            setAmenitiesWatchVideo(true);
            setDataMediaLink(
                interactiveVideoLink
                    ? interactiveVideoLink
                    : whyServiceHereVideoLink.link
            );
        }
        if (whyServiceHereLink && whyServiceHereLink.open_link_in_new_tab)
            setLinkTarget("_blank");
        // set device detect to track component and provide screen dimension to amenities local state.
        const key = window.window.deviceDetect.addStateAccessor({
            stateAccessor: setScreenSizes,
        });

        return () => {
            window.window.deviceDetect.removeStateAccessor({ key: key });
        };
    }, []);

    const commonDataAttributes = {
        ...dataAttributes,
        "data-amenitiessectionid": amenities.id,
        "data-name": amenities.name,
    };

    const gaDataAttributes = {
        "data-clickurl": amenitiesButtonLink,
        "data-clicklocation": "Amenities Watch Video Button",
        "data-clickindex": "Why Service Here Button",
    };

    return (
        <StyledContainer
            defaultFontFamily={defaultFontFamily}
            textColor={text_color ?? "#000000"}
            className="FODAmenitiesWrapper"
            ref={containerRef}
            isTableRow={isTableRow}
            bgColor={background_color}
            padding={padding}
            margin={margin}
        >
            {amenitiesButtonLink && amenitiesWatchVideo && (
                <WatchVideoModal
                    className="FODSidePanelWatchVideoModal"
                    activeVideo={activeVideo}
                    link={amenitiesButtonLink}
                    closeFunction={() => setActiveVideo(false)}
                />
            )}
            <StyledContentWrapper>
                <AmenitiesWrapper className="FODAmenitiesWrapper">
                    <Coffee
                        svgIconSize={75}
                        fill={"black"}
                        padding={0}
                        margin={0}
                    />
                    <Title
                        textColor={text_color}
                        letterSpacing={title_letter_spacing}
                        textTransform={text_transform}
                        id="FODAmenitiesHeader"
                    >
                        {formatText(rowData, "Our Amenities")}
                    </Title>
                    <StyledDivider />
                    <SubTitle
                        textColor={text_color}
                        letterSpacing={title_letter_spacing}
                        textTransform={text_transform}
                        className="FODAmenitiesHeader"
                    >
                        {header}
                    </SubTitle>
                    {amenitiesButtonLink ? (
                        <StyledButton
                            className="FODV5GAAmenitiesSectionClickEvent"
                            {...commonDataAttributes}
                            {...gaDataAttributes}
                            bgColor={button_background_color}
                            color={button_text_color}
                            hoverColor={button_text_hover_color}
                            hoverBgColor={button_background_hover_color}
                            borderColor={button_border_color}
                            borderRadius={button_border_radius}
                            letterSpacing={button_letter_spacing}
                            textTransform={text_transform}
                            onClick={handleVideoClick}
                            isIconVisible={amenitiesWatchVideo}
                            data-testid="amenities_cta_button"
                            data-clickurl={dataMediaLink}
                            data-asceventact={
                                amenitiesWatchVideo ? "open" : "redirect"
                            }
                            data-ascmediatype="video"
                        >
                            {amenitiesWatchVideo && !hide_cta_icons ? (
                                <WatchVideo svgIconSize={"25px"} />
                            ) : null}

                            {formatText(rowData, "Why Service Here?")}
                        </StyledButton>
                    ) : null}
                    {body_text && body_text !== "" ? (
                        <BodyText
                            textAlign={text_align}
                            textColor={text_color}
                            lineHeight={line_height}
                            letterSpacing={body_text_letter_spacing}
                            className="FODAmenitiesParagraph"
                        >
                            {body_text}
                        </BodyText>
                    ) : null}
                    <AmenityContainer
                        isSingleColumn={isSingleColumn}
                        className="FODAmenitiesListContainer"
                    >
                        <div
                            style={{ width: isSingleColumn ? "100%" : "unset" }}
                            className="FODAmenitiesColumn1"
                        >
                            {amenitiesFirstHalf.map((amenity, index) => {
                                const amenityProps = {
                                    links,
                                    dataAttributes: commonDataAttributes,
                                    color: button_background_color,
                                    linkColor: link_color,
                                    hoverColor: link_hover_color,
                                    textColor: text_color,
                                    amenity,
                                    key: index,
                                    clickindex: index + 1,
                                    divId,
                                    rowId,
                                };
                                return (
                                    <Amenity {...amenityProps}>
                                        {amenity.text}
                                    </Amenity>
                                );
                            })}
                        </div>

                        <div
                            style={{ width: isSingleColumn ? "100%" : "unset" }}
                            className="FODAmenitiesColumn2"
                        >
                            {amenitiesSecondHalf.map((amenity, index) => {
                                const amenityProps = {
                                    links,
                                    dataAttributes: commonDataAttributes,
                                    color: button_background_color,
                                    linkColor: link_color,
                                    hoverColor: link_hover_color,
                                    textColor: text_color,
                                    amenity,
                                    key: index,
                                    clickindex: index + 1,
                                    divId,
                                    rowId,
                                };
                                return (
                                    <Amenity {...amenityProps}>
                                        {amenity.text}
                                    </Amenity>
                                );
                            })}
                        </div>
                    </AmenityContainer>
                </AmenitiesWrapper>
            </StyledContentWrapper>
        </StyledContainer>
    );
}

export default Amenities;
